import {
  COMPLETION_NOTIFICATION_INBOX_KEY,
  onSessionRemoved,
} from "@/shared/lib/token/token"
import {
  type CompletionAlert,
  completionKey,
  completionPath,
  isAwayFromCompletion,
} from "./completion-alerts"

export const COMPLETION_INBOX_CHANGE = "completion-inbox-change"
export const COMPLETION_INBOX_LIMIT = 100

export type CompletionInboxEntry = CompletionAlert & {
  createDt: string
  isRead: boolean
  isSeen: boolean
}

let entries: readonly CompletionInboxEntry[] = []
let stored: string | null | undefined
let storageDirty = false

const valid = (entry: unknown): entry is CompletionInboxEntry => {
  if (!entry || typeof entry !== "object") return false
  const value = entry as Partial<CompletionInboxEntry>
  return (
    (value.kind === "assistant" ||
      value.kind === "review" ||
      value.kind === "cat-review") &&
    typeof value.resourceId === "string" &&
    value.resourceId.length > 0 &&
    typeof value.runId === "string" &&
    value.runId.length > 0 &&
    typeof value.createDt === "string" &&
    Number.isFinite(Date.parse(value.createDt)) &&
    typeof value.isRead === "boolean" &&
    typeof value.isSeen === "boolean"
  )
}

/** Stable snapshot for React; only identifiers and completion times belong in session storage. */
export const completionInboxEntries = () => {
  if (storageDirty) return entries
  try {
    const raw = sessionStorage.getItem(COMPLETION_NOTIFICATION_INBOX_KEY)
    if (raw !== stored) {
      const parsed: unknown = JSON.parse(raw ?? "null")
      entries = Array.isArray(parsed)
        ? parsed.filter(valid).slice(0, COMPLETION_INBOX_LIMIT)
        : []
      stored = raw
    }
  } catch {
    storageDirty = true
  }
  return entries
}

const publish = (next: readonly CompletionInboxEntry[]) => {
  entries = next
  try {
    stored = JSON.stringify(next)
    sessionStorage.setItem(COMPLETION_NOTIFICATION_INBOX_KEY, stored)
    storageDirty = false
  } catch {
    storageDirty = true
  }
  window.dispatchEvent(new Event(COMPLETION_INBOX_CHANGE))
}

export const subscribeCompletionInbox = (listener: () => void) => {
  window.addEventListener(COMPLETION_INBOX_CHANGE, listener)
  return () => window.removeEventListener(COMPLETION_INBOX_CHANGE, listener)
}

/** A re-evaluated target refreshes its row. Inbox retention never trims the run deduplication state. */
export const recordCompletionNotification = (entry: CompletionAlert) => {
  const isRead = !isAwayFromCompletion(entry)
  publish(
    [
      {
        kind: entry.kind,
        resourceId: entry.resourceId,
        runId: entry.runId,
        createDt: new Date().toISOString(),
        isRead,
        isSeen: isRead,
      },
      ...completionInboxEntries().filter(
        (item) => completionKey(item) !== completionKey(entry)
      ),
    ].slice(0, COMPLETION_INBOX_LIMIT)
  )
}

/** Seeing the list clears the bell indicator, while the result remains unread. */
export const markCompletionInboxSeen = () => {
  const current = completionInboxEntries()
  if (current.some((entry) => !entry.isSeen))
    publish(current.map((entry) => ({ ...entry, isSeen: true })))
}

export const acknowledgeCompletionInbox = (pathname: string) => {
  const path = pathname.replace(/\/$/, "")
  const current = completionInboxEntries()
  if (current.some((entry) => !entry.isRead && completionPath(entry) === path))
    publish(
      current.map((entry) =>
        completionPath(entry) === path
          ? { ...entry, isRead: true, isSeen: true }
          : entry
      )
    )
}

export const clearCompletionInboxResource = (
  kind: CompletionAlert["kind"],
  resourceId: string
) => {
  const current = completionInboxEntries()
  const next = current.filter(
    (entry) => entry.kind !== kind || entry.resourceId !== resourceId
  )
  if (next.length !== current.length) publish(next)
}

export const clearCompletionInbox = () => {
  entries = []
  try {
    sessionStorage.removeItem(COMPLETION_NOTIFICATION_INBOX_KEY)
    stored = null
    storageDirty = false
  } catch {
    storageDirty = true
  }
  window.dispatchEvent(new Event(COMPLETION_INBOX_CHANGE))
}

onSessionRemoved(clearCompletionInbox)
